//AssistantConfigEditor.tsx
import { useEffect, useState } from 'react';

export interface AssistantConfig {
  // Model settings
  modelProvider: string;
  modelName: string;
  temperature: number;

  // Voice settings
  voiceProvider: string;
  voiceId: string;

  // Transcriber settings
  transcriberProvider: string;
  transcriberModel: string;
  transcriberLanguage: string;

  // Message
  firstMessage: string;
}

export const DEFAULT_CONFIG: AssistantConfig = {
  modelProvider: 'openai',
  modelName: 'gpt-4o',
  temperature: 0.4,
  voiceProvider: 'vapi',
  voiceId: 'Mia',
  transcriberProvider: 'deepgram',
  transcriberModel: 'nova-2',
  transcriberLanguage: 'en',
  firstMessage: "Hi, I'm Kaiva. I'll run a structured handover interview. To start, what role are you currently in and what are your top responsibilities?",
};

// Available options for dropdowns
const MODEL_PROVIDERS = ['openai', 'anthropic'];
const OPENAI_MODELS = ['gpt-4o', 'gpt-4', 'gpt-4-turbo', 'gpt-3.5-turbo'];
const ANTHROPIC_MODELS = ['claude-3-opus', 'claude-3-sonnet', 'claude-3-haiku'];

const VOICE_PROVIDERS = ['vapi', '11labs', 'playht', 'deepgram'];
const VAPI_VOICES = [
  { id: 'Mia', name: 'Mia (Professional, Female)' },
  { id: 'Alex', name: 'Alex (Professional, Male)' },
  { id: 'Lily', name: 'Lily (Friendly, Female)' },
];
const ELEVENLABS_VOICES = [
  { id: 'rachel', name: 'Rachel (Professional, Warm)' },
  { id: 'clyde', name: 'Clyde (Professional, Male)' },
  { id: 'domi', name: 'Domi (Energetic)' },
  { id: 'dave', name: 'Dave (Natural, Male)' },
  { id: 'fin', name: 'Fin (British)' },
  { id: 'sarah', name: 'Sarah (Soft, Female)' },
];

const TRANSCRIBER_PROVIDERS = ['deepgram', 'gladia', 'talkscriber'];
const DEEPGRAM_MODELS = ['nova-2', 'nova', 'enhanced', 'base'];
const LANGUAGES = ['en', 'es', 'fr', 'de', 'it', 'pt', 'nl'];

interface AssistantConfigEditorProps {
  initialConfig?: Partial<AssistantConfig>;
  onChange: (config: AssistantConfig) => void;
  disabled?: boolean;
}

export function AssistantConfigEditor({
  initialConfig,
  onChange,
  disabled = false
}: AssistantConfigEditorProps) {
  const [config, setConfig] = useState<AssistantConfig>({
    ...DEFAULT_CONFIG,
    ...initialConfig,
  });

  const [activeSection, setActiveSection] = useState<'model' | 'voice' | 'transcriber' | 'message'>('model');

  useEffect(() => {
    onChange(config);
  }, [config, onChange]);

  const updateConfig = (updates: Partial<AssistantConfig>) => {
    setConfig((prev) => ({ ...prev, ...updates }));
  };

  const availableModels = config.modelProvider === 'openai' ? OPENAI_MODELS : ANTHROPIC_MODELS;

  return (
    <div className="space-y-4 rounded-xl border border-border bg-card p-5">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">
          Custom Kaiva Configuration
        </h3>
        <button
          type="button"
          onClick={() => setConfig(DEFAULT_CONFIG)}
          disabled={disabled}
          className="rounded-md border border-border bg-card px-2.5 py-1 text-xs font-semibold text-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
        >
          Reset to Defaults
        </button>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-1 rounded-lg border border-border bg-muted p-1">
        {(['model', 'voice', 'transcriber', 'message'] as const).map((section) => (
          <button
            key={section}
            type="button"
            onClick={() => setActiveSection(section)}
            disabled={disabled}
            className={`flex-1 rounded-md px-3 py-1.5 text-xs font-semibold capitalize transition ${
              activeSection === section
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground disabled:cursor-not-allowed'
            }`}
          >
            {section}
          </button>
        ))}
      </div>

      {/* Model Settings */}
      {activeSection === 'model' && (
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-foreground">
              Provider
            </label>
            <select
              value={config.modelProvider}
              onChange={(e) => {
                const provider = e.target.value;
                const newModel = provider === 'openai' ? 'gpt-4' : 'claude-3-sonnet';
                updateConfig({
                  modelProvider: provider,
                  modelName: newModel
                });
              }}
              disabled={disabled}
              className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/20 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {MODEL_PROVIDERS.map((provider) => (
                <option key={provider} value={provider}>
                  {provider}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold text-foreground">
              Model
            </label>
            <select
              value={config.modelName}
              onChange={(e) => updateConfig({ modelName: e.target.value })}
              disabled={disabled}
              className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/20 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {availableModels.map((model) => (
                <option key={model} value={model}>
                  {model}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 flex items-center justify-between text-xs font-semibold text-foreground">
              <span>Temperature</span>
              <span className="font-normal text-kaiva-success tabular-nums">{config.temperature}</span>
            </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={config.temperature}
              onChange={(e) => updateConfig({ temperature: parseFloat(e.target.value) })}
              disabled={disabled}
              className="w-full accent-kaiva-teal disabled:cursor-not-allowed disabled:opacity-50"
            />
            <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
              <span>Focused (0)</span>
              <span>Creative (1)</span>
            </div>
          </div>
        </div>
      )}

      {/* Voice Settings */}
      {activeSection === 'voice' && (
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-foreground">
              Voice Provider
            </label>
            <select
              value={config.voiceProvider}
              onChange={(e) => updateConfig({ voiceProvider: e.target.value })}
              disabled={disabled}
              className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/20 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {VOICE_PROVIDERS.map((provider) => (
                <option key={provider} value={provider}>
                  {provider}
                </option>
              ))}
            </select>
          </div>

          {config.voiceProvider === 'vapi' && (
            <div>
              <label className="mb-1 block text-xs font-semibold text-foreground">
                Voice
              </label>
              <select
                value={config.voiceId}
                onChange={(e) => updateConfig({ voiceId: e.target.value })}
                disabled={disabled}
                className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/20 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {VAPI_VOICES.map((voice) => (
                  <option key={voice.id} value={voice.id}>
                    {voice.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-muted-foreground">
                VAPI's built-in voices - optimized for low latency
              </p>
            </div>
          )}

          {config.voiceProvider === '11labs' && (
            <div>
              <label className="mb-1 block text-xs font-semibold text-foreground">
                Voice
              </label>
              <select
                value={config.voiceId}
                onChange={(e) => updateConfig({ voiceId: e.target.value })}
                disabled={disabled}
                className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/20 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {ELEVENLABS_VOICES.map((voice) => (
                  <option key={voice.id} value={voice.id}>
                    {voice.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-muted-foreground">
                Preview voices at{' '}
                <a
                  href="https://elevenlabs.io/voice-library"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-kaiva-success underline hover:text-kaiva-success"
                >
                  ElevenLabs Voice Library
                </a>
              </p>
            </div>
          )}

          {config.voiceProvider !== 'vapi' && config.voiceProvider !== '11labs' && (
            <div>
              <label className="mb-1 block text-xs font-semibold text-foreground">
                Voice ID
              </label>
              <input
                type="text"
                value={config.voiceId}
                onChange={(e) => updateConfig({ voiceId: e.target.value })}
                disabled={disabled}
                placeholder="Enter voice ID"
                className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/70 focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/20 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
          )}
        </div>
      )}

      {/* Transcriber Settings */}
      {activeSection === 'transcriber' && (
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-foreground">
              Transcriber Provider
            </label>
            <select
              value={config.transcriberProvider}
              onChange={(e) => updateConfig({ transcriberProvider: e.target.value })}
              disabled={disabled}
              className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/20 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {TRANSCRIBER_PROVIDERS.map((provider) => (
                <option key={provider} value={provider}>
                  {provider}
                </option>
              ))}
            </select>
          </div>

          {config.transcriberProvider === 'deepgram' && (
            <div>
              <label className="mb-1 block text-xs font-semibold text-foreground">
                Model
              </label>
              <select
                value={config.transcriberModel}
                onChange={(e) => updateConfig({ transcriberModel: e.target.value })}
                disabled={disabled}
                className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/20 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {DEEPGRAM_MODELS.map((model) => (
                  <option key={model} value={model}>
                    {model}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="mb-1 block text-xs font-semibold text-foreground">
              Language
            </label>
            <select
              value={config.transcriberLanguage}
              onChange={(e) => updateConfig({ transcriberLanguage: e.target.value })}
              disabled={disabled}
              className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/20 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {LANGUAGES.map((lang) => (
                <option key={lang} value={lang}>
                  {lang}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* First Message */}
      {activeSection === 'message' && (
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-foreground">
              First Message
            </label>
            <textarea
              value={config.firstMessage}
              onChange={(e) => updateConfig({ firstMessage: e.target.value })}
              disabled={disabled}
              rows={4}
              className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/70 focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/20 disabled:cursor-not-allowed disabled:opacity-50"
              placeholder="Enter the first message Kaiva will say..."
            />
            <p className="mt-1 text-xs text-muted-foreground">
              This is what Kaiva will say when the interview starts.
            </p>
          </div>
        </div>
      )}

      {/* Info box */}
      <div className="rounded-lg border border-kaiva-success/30 bg-kaiva-teal/10 p-3">
        <p className="text-xs text-kaiva-teal">
          <strong>Custom Mode:</strong> Creates a new VAPI assistant with these settings when
          you start the interview. Great for experimenting!
        </p>
      </div>
    </div>
  );
}
